import { saveAs } from 'file-saver';

import { enqueueSnackbar } from '../notificationActions';
import { EnumPaymentStatus, EnumProcessStatus, SnackbarMessageVariants } from '../../../helpers/enums';
import ApiService from '../../../services/apiService';
import Endpoints from '../../../services/endpoints';
import { PageSortParams } from '../../../interfaces/pageSortParams';

export const PROCESS_RATES_TEMPLATE_REQUEST = 'PROCESS_RATES_TEMPLATE_REQUEST';
export const PROCESS_RATES_TEMPLATE_SUCCESS = 'PROCESS_RATES_TEMPLATE_SUCCESS';
export const PROCESS_RATES_TEMPLATE_FAILURE = 'PROCESS_RATES_TEMPLATE_FAILURE';

export const VERIFY_PROCESS_RATES_REQUEST = 'VERIFY_PROCESS_RATES_REQUEST';
export const VERIFY_PROCESS_RATES_SUCCESS = 'VERIFY_PROCESS_RATES_SUCCESS';
export const VERIFY_PROCESS_RATES_FAILURE = 'VERIFY_PROCESS_RATES_FAILURE';

export const SUBMIT_PROCESS_RATES_REQUEST = 'SUBMIT_PROCESS_RATES_REQUEST';
export const SUBMIT_PROCESS_RATES_SUCCESS = 'SUBMIT_PROCESS_RATES_SUCCESS';
export const SUBMIT_PROCESS_RATES_FAILURE = 'SUBMIT_PROCESS_RATES_FAILURE';

export const PROCESS_RATES_HISTORY_REQUEST = 'PROCESS_RATES_HISTORY_REQUEST';
export const PROCESS_RATES_HISTORY_SUCCESS = 'PROCESS_RATES_HISTORY_SUCCESS';
export const PROCESS_RATES_HISTORY_FAILURE = 'PROCESS_RATES_HISTORY_FAILURE';

export const PROCESSED_RATES_REQUEST = 'PROCESSED_RATES_REQUEST';
export const PROCESSED_RATES_SUCCESS = 'PROCESSED_RATES_SUCCESS';
export const PROCESSED_RATES_FAILURE = 'PROCESSED_RATES_FAILURE';

export const PROCESS_RATES_IS_PENDING = 'PROCESS_RATES_IS_PENDING';

export const SET_ACTIVE_STEP = 'SET_ACTIVE_STEP';

const DOWNLOAD_TYPE = 'text/csv;charset=utf-8';
const TEMPLATE_FILE_NAME = 'processRateTemplate.csv';

export interface InvalidRecordReason {
    rowNumber: number;
    errorMessage: string;
}

export interface VerifiedSummary {
    estimatedCostForAllRows: number;
    estimatedCostForAllValidRows: number;
    invalidRecords: InvalidRecordReason[];
    totalInvalidRecords: number;
    totalRecordsProcessed: number;
    totalValidRecords: number;
}

export interface FileStatus {
    id: number;
    userId: string;
    startTime: number;
    endTime: number;
    processingStatus: string;
    message: string;
    inputFileName: string;
    inputFilePath: string;
    outputFileName: string;
    outputFilePath: string;
    inputFileLastModified: Date;
    inputFileETag: number;
    outputFileLastModified: Date;
    outputFileETag: number;
    userEmailAddress: string;
    paymentStatus: string;
    processedRowsFuel: number;
    processedRowsSpread: number;
    processedRowsMri: number;
    fileDeletedFlag: boolean;
    guid: string;
}

export interface FileHistory {
    content: FileStatus[];
    empty: boolean;
    numberOfElements: number;
    offset: number;
    pageNumber: number;
    pageable: any;
    size: number;
    totalPages: number;
    totalSize: number;
    errorMessage?: string;
}

export interface VerifyParams {
    isSpread: boolean;
    isFuel: boolean;
    defaultEquipment: string;
    files: File[];
}

export interface FileHistoryParams {
    fileName?: string;
    page: PageSortParams;
}

export const setActiveStep = (activeStep: number): object => {
    return {
        type: SET_ACTIVE_STEP,
        payload: activeStep
    };
};

const requestProcessRatesTemplate = (): object => {
    return {
        type: PROCESS_RATES_TEMPLATE_REQUEST
    };
};

const receiveProcessRatesTemplate = (): object => {
    return {
        type: PROCESS_RATES_TEMPLATE_SUCCESS
    };
};

const requestProcessRatesTemplateFailed = (): object => {
    return {
        type: PROCESS_RATES_TEMPLATE_FAILURE
    };
};

export const downloadProcessRatesTemplate = (): object => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestProcessRatesTemplate());

        try {
            const rawData = await ApiService.get(Endpoints.downloadRateProcessTemplate, true);

            const blob = new Blob([rawData], { type: DOWNLOAD_TYPE });
            saveAs(blob, TEMPLATE_FILE_NAME);

            dispatch(receiveProcessRatesTemplate());
        } catch (err) {
            dispatch(requestProcessRatesTemplateFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while downloading rate template.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};

const requestVerifyProcessRates = (params: VerifyParams): object => {
    return {
        type: VERIFY_PROCESS_RATES_REQUEST,
        payload: params
    };
};

const recieveVerifyProcessRates = (verifiedSummary: VerifiedSummary): object => {
    return {
        type: VERIFY_PROCESS_RATES_SUCCESS,
        payload: verifiedSummary
    };
};

const requestVerifyProcessRatesFailed = (): object => {
    return {
        type: VERIFY_PROCESS_RATES_FAILURE
    };
};

export const verifyProcessRates = (params: VerifyParams): object => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestVerifyProcessRates(params));

        try {
            const formData = new FormData();

            if (params.files.length !== 0) {
                formData.append('file', new Blob([params.files[0]], { type: 'text/csv' }), params.files[0].name);

                const verifiedSummary: VerifiedSummary = await ApiService.post(`${Endpoints.verifyProcessRates}?isSpread=${params.isSpread}&isFuel=` +
                    `${params.isFuel}&defaultEquipment=${params.defaultEquipment}`, formData, true);

                dispatch(setActiveStep(1));
                dispatch(recieveVerifyProcessRates(verifiedSummary));
                dispatch(enqueueSnackbar({
                    message: 'File verified.',
                    variant: SnackbarMessageVariants.Success
                }));
            } else {
                throw new Error('File is not selected');
            }
        } catch (err) {
            dispatch(requestVerifyProcessRatesFailed());
            if (Math.floor(err?.response?.data?.errorCode / 100) === 4) {
                dispatch(enqueueSnackbar({
                    message: err.response.data.message,
                    variant: SnackbarMessageVariants.Error
                }));
            } else {
                dispatch(enqueueSnackbar({
                    message: 'Error occurred while verifying rates.',
                    variant: SnackbarMessageVariants.Error
                }));
            }
        }
    };
};

const requestSubmitProcessRates = (params: VerifyParams): object => {
    return {
        type: SUBMIT_PROCESS_RATES_REQUEST,
        payload: params
    };
};

const recieveSubmitProcessRates = (fileStatus: FileStatus): object => {
    return {
        type: SUBMIT_PROCESS_RATES_SUCCESS,
        payload: fileStatus
    };
};

const requestSubmitProcessRatesFailed = (): object => {
    return {
        type: SUBMIT_PROCESS_RATES_FAILURE
    };
};

export const submitProcessRates = (params: VerifyParams, resetFormCallback: Function|undefined = undefined): object => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestSubmitProcessRates(params));

        try {
            const formData = new FormData();

            if (params.files.length !== 0) {
                formData.append('file', new Blob([params.files[0]], { type: 'text/csv' }), params.files[0].name);

                const fileStatus: FileStatus = await ApiService.post(`${Endpoints.submitProcessRates}?isSpread=${params.isSpread}&isFuel=` +
                    `${params.isFuel}&defaultEquipment=${params.defaultEquipment}`, formData, true);

                dispatch(setActiveStep(0));
                dispatch(recieveSubmitProcessRates(fileStatus));
                dispatch(enqueueSnackbar({
                    message: 'File uploaded.',
                    variant: SnackbarMessageVariants.Success
                }));
                if (resetFormCallback !== undefined) {
                    resetFormCallback();
                }
            } else {
                dispatch(setActiveStep(0));
                throw new Error('File is not selected');
            }
        } catch (err) {
            dispatch(requestSubmitProcessRatesFailed());
            if (Math.floor(err?.response?.data?.errorCode / 100) === 4) {
                dispatch(enqueueSnackbar({
                    message: err.response.data.message,
                    variant: SnackbarMessageVariants.Error
                }));
            } else {
                dispatch(enqueueSnackbar({
                    message: 'Error occurred while submitting rates.',
                    variant: SnackbarMessageVariants.Error
                }));
            }
            if (resetFormCallback !== undefined) {
                resetFormCallback();
            }
        }
    };
};

const requestProcessRatesHistory = (params: FileHistoryParams): object => {
    return {
        type: PROCESS_RATES_HISTORY_REQUEST,
        payload: params
    };
};

const recieveProcessRatesHistory = (files: FileHistory): object => {
    return {
        type: PROCESS_RATES_HISTORY_SUCCESS,
        payload: files
    };
};

const requestProcessRatesHistoryFailed = (): object => {
    return {
        type: PROCESS_RATES_HISTORY_FAILURE
    };
};

const pendingFiles = (isPending: boolean): object => {
    return {
        type: PROCESS_RATES_IS_PENDING,
        payload: isPending
    };
};

const isAnyPendingFiles = (fileHistory: FileHistory): boolean => {
    if (!fileHistory || !Array.isArray(fileHistory.content)) {
        return false;
    }
    for (let i = 0; i < fileHistory.content.length; i += 1) {
        const item = fileHistory.content[i];

        if (
            (
                item.paymentStatus !== EnumPaymentStatus.Completed &&
                item.paymentStatus !== EnumPaymentStatus.Failed) ||
            (
                item.processingStatus !== EnumProcessStatus.Completed &&
                item.processingStatus !== EnumProcessStatus.Failed
            )
        ) {
            return true;
        }
    }

    return false;
};

export const fetchProcessRatesHistory = (params: FileHistoryParams): object => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestProcessRatesHistory(params));

        try {
            const fileHistory: FileHistory = await ApiService.get(
                encodeURI(`${Endpoints.getProcessRateHistory}?page=${params.page.currentPage}` +
                    `&size=${params.page.pageSize}&sort=${params.page.sortField},${params.page.sortDirection}` +
                    `&fileName=${params.fileName ? params.fileName : ''}`),
                true
            );

            if (isAnyPendingFiles(fileHistory)) {
                dispatch(pendingFiles(true));
            } else {
                dispatch(pendingFiles(false));
            }

            dispatch(recieveProcessRatesHistory(fileHistory));
        } catch (err) {
            dispatch(requestProcessRatesHistoryFailed());
            if (Math.floor(err?.response?.data?.errorCode / 100) === 4) {
                dispatch(enqueueSnackbar({
                    message: err.response.data.message,
                    variant: SnackbarMessageVariants.Info
                }));
            } else {
                dispatch(enqueueSnackbar({
                    message: 'Error occurred while fetching rates history.',
                    variant: SnackbarMessageVariants.Error
                }));
            }
        }
    };
};

const requestProcessedRates = (): object => {
    return {
        type: PROCESSED_RATES_REQUEST
    };
};

const receiveProcessedRates = (): object => {
    return {
        type: PROCESSED_RATES_SUCCESS
    };
};

const requestProcessedRatesFailed = (): object => {
    return {
        type: PROCESSED_RATES_FAILURE
    };
};

export const downloadProcessedRates = (fileName: string): object => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestProcessedRates());
        try {
            const encodedFileName = encodeURIComponent(fileName);
            const rawData = await ApiService.get(`${Endpoints.downloadProcessedRates}/${encodedFileName}`, true);

            const blob = new Blob([rawData], { type: DOWNLOAD_TYPE });
            saveAs(blob, fileName);

            dispatch(receiveProcessedRates());
        } catch (err) {
            dispatch(requestProcessedRatesFailed());
            dispatch(enqueueSnackbar({
                message: `Error occurred while downloading ${fileName}.`,
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};
